import { MapPath } from "@/shared/state/visualization/schema/v1";
import { AsyncEvent } from "@/shared/synchronization";
import { MessagePayload, WorkerManager } from "@/shared/webworker";

import { CoordinateValidationError } from "./coordinateValidation";
import { getGeoMessagePaths } from "./geoTypes";
import { MapPathData } from "./MapPathData";
import {
  type MapLoadingState,
  type MapPathDataSource,
} from "./MapPathDataSource";
import {
  isInitializedEvent,
  isPathCoordinatesResponse,
  isPathDataCompleteResponse,
  MapDataCommand,
  SOURCE,
} from "./messaging";

/** A path's current load: its id and the coordinates received for it. */
interface PathLoad {
  loadId: string;
  data: MapPathData;
}

/**
 * Owns the worker that reads a map panel's latitude and longitude topics, and
 * the coordinate buffers it fills, one per path the panel draws. Coordinates
 * arrive in chunks over the panel's lifetime, so subscribers register
 * callbacks rather than await a result.
 *
 * Implements {@link MapPathDataSource} for the visualizer, whose paths carry
 * the `TopicData` they are bound to.
 */
export class MapDataManager implements MapPathDataSource {
  #workerManager: WorkerManager;
  #loadByPathId: Map<string, PathLoad> = new Map();
  #onPathUpdated?: (pathId: string) => void;
  #onPathComplete?: (pathId: string) => void;
  #onError?: (error: Error) => void;
  #onLoadingStateChange?: (state: MapLoadingState) => void;
  #abortController: AbortController;
  #initialized = new AsyncEvent();

  constructor({ onError }: { onError: (error: Error) => void }) {
    this.#onError = onError;
    this.#abortController = new AbortController();

    const worker = new Worker(
      new URL("./MapDataService.worker", import.meta.url),
      { type: "module", name: `MapDataService_${crypto.randomUUID()}` },
    );

    this.#workerManager = new WorkerManager({
      worker,
      onError: (err) => {
        this.#onLoadingStateChange?.("complete");
        this.#onError?.(err);
      },
      onMessage: this.#onMessage,
      signal: this.#abortController.signal,
    });

    // Send init command
    this.#workerManager.sendCommand({
      data: undefined,
      source: SOURCE,
      type: MapDataCommand.Init,
    });
  }

  /**
   * Start streaming a path's coordinates from the worker and put the panel in
   * "loading-first-chunk", which shows a loading overlay across the panel until
   * the first chunk arrives. Called when a user adds a path to the panel, and again for a path
   * whose coordinate units the user changed.
   *
   * One topic reader feeds both coordinates, so latitude and longitude must
   * come from the same topic; a path naming two is refused and reported
   * through onError.
   *
   * Each call starts a new load of the path and has the worker stop any
   * earlier one; coordinates the earlier load sent before it stopped are
   * dropped as they arrive.
   */
  loadPathData(path: MapPath): void {
    const { latMessagePath, lonMessagePath, latTopicData, lonTopicData } =
      getGeoMessagePaths(path);

    const latTopic = latTopicData.topic;
    const lonTopic = lonTopicData.topic;

    if (latTopic.id !== lonTopic.id) {
      this.#onError?.(
        new CoordinateValidationError(
          `Latitude and longitude must come from the same topic. Got topics "${latTopic.name}" and "${lonTopic.name}".`,
        ),
      );
      return;
    }

    const topicId = latTopic.id;
    const latMessagePathId = latTopicData.messagePath.id;
    const lonMessagePathId = lonTopicData.messagePath.id;

    const previous = this.#loadByPathId.get(path.id);
    if (previous !== undefined) {
      this.#releaseLoad(previous);
    }
    const loadId = crypto.randomUUID();
    this.#loadByPathId.set(path.id, { loadId, data: new MapPathData() });

    this.#onLoadingStateChange?.("loading-first-chunk");

    this.#workerManager.sendCommand({
      data: {
        pathId: path.id,
        loadId,
        topicId,
        latMessagePath,
        lonMessagePath,
        latMessagePathId,
        lonMessagePathId,
      },
      source: SOURCE,
      type: MapDataCommand.LoadPathData,
    });
  }

  /** Get the MapPathData instance for a path */
  getPathData(pathId: string): MapPathData | undefined {
    return this.#loadByPathId.get(pathId)?.data;
  }

  /**
   * Drop a path's coordinates, and have the worker stop the path's load and
   * release its topic reader, after a user removes the path from the panel
   * or changes its coordinate units.
   */
  disposePath(path: MapPath): void {
    const load = this.#loadByPathId.get(path.id);
    this.#loadByPathId.delete(path.id);

    // A user can remove the path the panel is still waiting on. Loading state
    // is otherwise recomputed only when a path reports its last chunk, which a
    // removed path never will, so the panel would hold playback for a load that
    // will never land.
    this.#recomputeLoadingState();

    if (load !== undefined) {
      this.#releaseLoad(load);
    }
  }

  /** Dispose the manager and worker */
  dispose(): void {
    this.#abortController.abort();
    this.#workerManager.dispose();
    this.#loadByPathId.clear();
  }

  /**
   * Register a callback fired once a path has received its last chunk, after
   * that path's MapPathData is marked complete, so the callback may read
   * `isComplete`. It carries no new coordinates: a subscriber that draws
   * chunks as they arrive uses it to draw whatever it held back while more was
   * still expected.
   */
  setOnPathComplete(callback: (pathId: string) => void): void {
    this.#onPathComplete = callback;
  }

  /** Register callback fired after each chunk is received */
  setOnPathUpdated(callback: (pathId: string) => void): void {
    this.#onPathUpdated = callback;
  }

  /** Returns a promise that resolves when the worker is initialized */
  waitForInitialized(): Promise<boolean> {
    return this.#initialized.wait();
  }

  /** Register loading state callback */
  setOnLoadingStateChange(callback: (state: MapLoadingState) => void): void {
    this.#onLoadingStateChange = callback;
  }

  /**
   * Announce whether any path held right now is still loading. A panel holding
   * no paths reports "complete" rather than waiting: `every` over an empty map
   * is true.
   */
  #recomputeLoadingState(): void {
    const allComplete = [...this.#loadByPathId.values()].every(
      ({ data }) => data.isComplete,
    );
    this.#onLoadingStateChange?.(
      allComplete ? "complete" : "loading-later-chunks",
    );
  }

  #releaseLoad({ loadId }: PathLoad): void {
    this.#workerManager.sendCommand({
      data: { loadId },
      source: SOURCE,
      type: MapDataCommand.ReleaseLoad,
    });
  }

  /** The coordinates of `pathId`'s current load, if `loadId` names it. */
  #currentPathData(pathId: string, loadId: string): MapPathData | undefined {
    const load = this.#loadByPathId.get(pathId);
    return load?.loadId === loadId ? load.data : undefined;
  }

  #onMessage = (event: MessageEvent<MessagePayload>): void => {
    if (isPathCoordinatesResponse(event)) {
      const { pathId, loadId, latitudes, longitudes, logTimes } =
        event.data.data;
      const pathData = this.#currentPathData(pathId, loadId);
      // The worker can still have chunks in flight for a path the user has
      // removed, or from an earlier load of a path the user has changed.
      // Drop them silently rather than announce a chunk that was never stored
      // or reopen the loading state for a panel that has finished.
      if (!pathData) {
        return;
      }

      // appendChunk rejects parallel arrays of differing length. The worker
      // pushes the three in lockstep so it cannot produce that today, but the
      // boundary's type guard reads only event.data.type and never the payload
      // shape, so the invariant holds by convention. Route a breach the same
      // way the worker's own errors are routed: announce "complete" first, or
      // the hold this load placed on the workspace timer is never released and
      // playback stays frozen with no error shown.
      try {
        pathData.appendChunk(latitudes, longitudes, logTimes);
      } catch (err) {
        this.#onLoadingStateChange?.("complete");
        this.#onError?.(
          err instanceof Error
            ? err
            : new Error("Failed to append map coordinate chunk", {
                cause: err,
              }),
        );
        return;
      }

      this.#onPathUpdated?.(pathId);
      this.#onLoadingStateChange?.("loading-later-chunks");
      return;
    }

    if (isPathDataCompleteResponse(event)) {
      const { pathId, loadId, totalCount } = event.data.data;
      // A completion can arrive for a path the user has already removed, or
      // from an earlier load, as a chunk can above; there is no path to mark
      // complete for it.
      const pathData = this.#currentPathData(pathId, loadId);
      if (pathData) {
        pathData.markComplete(totalCount);
        this.#onPathComplete?.(pathId);
      }

      this.#recomputeLoadingState();
      return;
    }

    if (isInitializedEvent(event)) {
      this.#initialized.set();
      return;
    }
  };
}
